import { Component, inject, Signal, OnInit, effect, computed } from "@angular/core";
import { RouterOutlet, NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs/operators";
import { PageLoaderComponent } from "./components/atoms/page-loader/page-loader.component";
import { PageLoaderService } from "./services";
import { LogoutStateService } from "./services/state/logout-state.service";
import { PathwayStateService } from "./services/state/pathway-state.service";
import { PathwayStatusService } from "./services/state/pathway-status.service";
import { HlmToasterComponent } from "@spartan-ng/ui-sonner-helm";
import { IntercomService } from "./services/intercom.service";
import { environment } from "./environments/environment";
import { LoggedUserService, AuthService } from "@keus-template/auth";

@Component({
  selector: "app-root",
  imports: [RouterOutlet, PageLoaderComponent, HlmToasterComponent],
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.css",
})
export class AppComponent implements OnInit {
  /** Page loader service for managing loading states */
  private readonly _pageLoaderService: PageLoaderService = inject(PageLoaderService);
  
  /** Intercom service for chat widget */
  private readonly _intercomService: IntercomService = inject(IntercomService);
  
  /** Logged user service for accessing current user */
  private readonly _loggedUserService: LoggedUserService = inject(LoggedUserService);
  
  /** Auth service for accessing user role from Cognito token */
  private readonly _authService: AuthService = inject(AuthService);
    
  /** Logout state service for managing logout flow */
  private readonly _logoutStateService: LogoutStateService = inject(LogoutStateService);

  /** Router for navigation events */
  private readonly _router: Router = inject(Router);

  /** Pathway state service for managing pathway data */
  private readonly _pathwayStateService: PathwayStateService = inject(PathwayStateService);

  /** Pathway status service for pathway status management */
  private readonly _pathwayStatusService: PathwayStatusService = inject(PathwayStatusService);


  /** Loading state signal */
  public readonly isLoading: Signal<boolean> = this._pageLoaderService.isLoading;
    
  /**
   * Signal that controls whether to show router content
   * Returns false during logout to prevent component initialization
   */
  public readonly canShowContent: Signal<boolean> = computed(() => 
    !this._logoutStateService.isLoggingOut()
  );

  constructor() {
    // Use effect to watch for user changes and initialize Intercom
    effect(() => {
      const user = this._loggedUserService.currentUser();
      
      // Only initialize if we have a real Intercom App ID (not test placeholders)
      if (user && environment.intercomAppId && 
          !environment.intercomAppId.startsWith('test_app_id')) {
        // Small delay to ensure all user data is loaded
        setTimeout(() => {
          this._intercomService.initializeIntercom(environment.intercomAppId!);
        }, 1000);
      } else if (environment.intercomAppId?.startsWith('test_app_id')) {
      }
    });

  }

  /**
   * Component initialization
   */
  ngOnInit(): void {
    // Subscribe to router navigation events for pathway data refresh
    this._router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this._refreshPathwayData();
      });

    // Initial pathway data load
    this._refreshPathwayData();
  }

  /**
   * Refreshes pathway data when user navigates between pages.
   * Ensures pathway status is always up-to-date across the application.
   * Only loads pathway data for patient users to avoid 404 errors.
   * Uses async role checking for timing robustness.
   */
  private _refreshPathwayData(): void {
    const user = this._loggedUserService.currentUser();
    
    // Don't proceed if user is not loaded yet
    if (!user?.id) {
      return;
    }
    
    // Use async role checking with proper session handling
    this._checkUserRoleAndLoadPathway();
  }

  /**
   * Async method to check user role and load pathway data if patient
   */
  private async _checkUserRoleAndLoadPathway(): Promise<void> {
    
    try {
      // Use new async method that properly waits for session
      const role = await this._authService.getCurrentUserRoleAsync();

      if (role === 'patient') {
        this._pathwayStateService.load();
      } else {
      }

    } catch (error) {
      // Silently handle errors - pathway data won't load but app continues working
    }
  }

}
